
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

export default function HomePage() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const logoSection = useInView(0.2);
  const overviewSection = useInView(0.2);
  const missionSection = useInView(0.2);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e6563a7edd8760fa0901f5abf8485665.jpg"
            alt="Clean Water Environment"
            className={`w-full h-full object-cover object-top transition-transform duration-[2000ms] ease-out ${heroLoaded ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className={`relative z-10 text-center text-white px-6 max-w-4xl mx-auto transition-all duration-1000 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-6xl font-bold mb-6 tracking-tight">HOME</h1>
        </div>
        
        {/* Animated scroll indicator */}
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/80 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Logo and Company Info Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div 
            ref={logoSection.ref}
            className={`flex justify-center mb-12 transition-all duration-1000 ease-out ${logoSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <img 
              src="https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/7babd17d092defa5310c852007a2bae2.png"
              alt="Bromine Systems Inc."
              className="max-w-md w-full h-auto hover:scale-105 transition-transform duration-500 cursor-pointer"
            />
          </div>

          {/* Overview Section */}
          <div 
            ref={overviewSection.ref}
            className={`mb-12 transition-all duration-1000 ease-out delay-100 ${overviewSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
              Overview:
              <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${overviewSection.isInView ? 'w-full' : 'w-0'}`}></span>
            </h2>
            <div className="space-y-4 text-gray-700 text-base leading-relaxed">
              <p className={`transition-all duration-700 delay-200 ${overviewSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Helping lend a leading hand in the market production of <strong className="text-teal-700">Bromine delivery systems</strong> and <strong className="text-teal-700">Bromine Cartridges</strong>--creating safe, potable drinking water for the marine environment, is what we're all about.
              </p>
              <p className={`transition-all duration-700 delay-300 ${overviewSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Consumers are at the heart of our operations. Promoting and inspiring a wealth of knowledge of safe drinking water and a healthy life style--and what a difference that truly makes! It's a responsibility we're more than happy to take on.
              </p>
              <p className={`transition-all duration-700 delay-400 ${overviewSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Our known products are trusted as they have been proven to provide potable, safe drinking water in many venues including but not limited to <strong className="text-teal-700">Offshore Oil Rigs and Gas Platforms</strong>, <strong className="text-teal-700">U.S. Navy surface and sub-surface vessels</strong>, <strong className="text-teal-700">U.S. Coast Guard</strong>, <strong className="text-teal-700">U.S. Army Corps of Engineers</strong> and <strong className="text-teal-700">Supply Vessels</strong> around the world.
              </p>
            </div>
          </div>

          {/* Mission Statement Section */}
          <div 
            ref={missionSection.ref}
            className={`mb-12 transition-all duration-1000 ease-out ${missionSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
              Mission Statement:
              <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${missionSection.isInView ? 'w-full' : 'w-0'}`}></span>
            </h2>
            <div className={`bg-gradient-to-r from-teal-50 to-white p-6 rounded-lg border-l-4 border-teal-600 transition-all duration-700 delay-200 ${missionSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <p className="text-gray-700 text-base leading-relaxed italic">
                Bromine Systems Inc. strives to work as a partnership with our Marine Community to help promote an effective means for bringing safe, potable drinking water to those around our country and the world.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${missionSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link 
              to="/contact-us"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Get In Touch
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
